/**
 * Campaigns Management Component
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { QrCode, Plus, Copy, Trash, Link2 } from "lucide-react";
import { authAxios } from "@/utils/auth";

const APP_URL = "https://instaquote-cleaning.preview.emergentagent.com";

export default function CampaignsManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignStats, setCampaignStats] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    code: "",
    name: "",
    description: "",
    neighborhood: ""
  });

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/campaigns");
      setCampaigns(res.data);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaignStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/campaigns/stats/summary");
      setCampaignStats(res.data);
    } catch (error) {
      // Stats are non-critical - log but don't show toast
      console.warn("Failed to load campaign stats:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchCampaignStats();
  }, [fetchCampaigns, fetchCampaignStats]);

  const createCampaign = useCallback(async () => {
    if (!newCampaign.code || !newCampaign.name) {
      toast.error("Code and name are required");
      return;
    }

    try {
      await authAxios.post("/campaigns", newCampaign);
      toast.success("Campaign created!");
      setNewCampaign({ code: "", name: "", description: "", neighborhood: "" });
      setShowAddForm(false);
      fetchCampaigns();
      fetchCampaignStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create campaign");
    }
  }, [newCampaign, fetchCampaigns, fetchCampaignStats]);

  const toggleCampaign = useCallback(async (campaignId, isActive) => {
    try {
      await authAxios.patch(`/campaigns/${campaignId}`, { is_active: !isActive });
      toast.success(`Campaign ${!isActive ? 'activated' : 'deactivated'}`);
      fetchCampaigns();
    } catch {
      toast.error("Failed to update campaign");
    }
  }, [fetchCampaigns]);

  const deleteCampaign = useCallback(async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    
    try {
      await authAxios.delete(`/campaigns/${campaignId}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
      fetchCampaignStats();
    } catch {
      toast.error("Failed to delete campaign");
    }
  }, [fetchCampaigns, fetchCampaignStats]);

  const copyQRLink = useCallback((code) => {
    const qrUrl = `${APP_URL}/api/go/${code}`;
    navigator.clipboard.writeText(qrUrl);
    toast.success("QR link copied to clipboard!");
  }, []);

  return (
    <div data-testid="campaigns-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Campaign Tracking
        </h1>
        <Button onClick={() => setShowAddForm(!showAddForm)} data-testid="add-campaign-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats Cards */}
      {campaignStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Campaigns</p>
              <p className="text-2xl font-bold text-slate-900">{campaignStats.total_campaigns}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{campaignStats.active_campaigns}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Quotes Generated</p>
              <p className="text-2xl font-bold text-blue-600">{campaignStats.total_campaign_quotes}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Campaign Form */}
      {showAddForm && (
        <Card className="border-slate-200 mb-6" data-testid="add-campaign-form">
          <CardHeader>
            <CardTitle>Create New Campaign</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Campaign Code</Label>
                <Input
                  placeholder="e.g., spring-2026-oakbrook"
                  value={newCampaign.code}
                  onChange={(e) => setNewCampaign({ ...newCampaign, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  data-testid="campaign-code-input"
                />
                <p className="text-xs text-slate-500 mt-1">Lowercase letters, numbers, hyphens, underscores only</p>
              </div>
              <div>
                <Label>Campaign Name</Label>
                <Input
                  placeholder="Spring 2026 - Oak Brook"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  data-testid="campaign-name-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Neighborhood</Label>
                <Input
                  placeholder="Oak Brook, IL"
                  value={newCampaign.neighborhood}
                  onChange={(e) => setNewCampaign({ ...newCampaign, neighborhood: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Door hanger campaign for..."
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={createCampaign} data-testid="create-campaign-btn">Create Campaign</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No campaigns yet</p>
            <p className="text-sm text-slate-400 mt-2">Create a campaign to generate trackable QR codes for door hangers</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className={`border-slate-200 ${!campaign.is_active ? 'opacity-60' : ''}`} data-testid={`campaign-card-${campaign.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-slate-900">{campaign.name}</h3>
                      <Badge className={campaign.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline" className="font-mono">
                        {campaign.code}
                      </Badge>
                    </div>
                    {campaign.neighborhood && (
                      <p className="text-sm text-slate-600 mb-2">📍 {campaign.neighborhood}</p>
                    )}
                    {campaign.description && (
                      <p className="text-sm text-slate-500 mb-2">{campaign.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-blue-600 font-medium">
                        {campaign.quotes_count || 0} quotes generated
                      </span>
                      <div className="flex items-center gap-1 text-slate-500">
                        <Link2 className="w-4 h-4" />
                        <code className="text-xs">{APP_URL}/api/go/{campaign.code}</code>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyQRLink(campaign.code)}
                      data-testid={`copy-qr-${campaign.code}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${APP_URL}/api/go/${campaign.code}`)}`, '_blank')}
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      QR Code
                    </Button>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Active</Label>
                      <Switch
                        checked={campaign.is_active}
                        onCheckedChange={() => toggleCampaign(campaign.id, campaign.is_active)}
                        data-testid={`toggle-campaign-${campaign.id}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCampaign(campaign.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
